import { Component, ElementRef, Input, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { Surface } from '@progress/kendo-drawing';

import { HeatmapService } from '../../../../services/heatmap.service';
import { LayerService } from '../../../../services/layer.service';
import { HeatmapMode, IMapcoreFeatureLayer } from '../../../../types';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-heatmap-editor-ng',
    templateUrl: './heatmap-editor.component.html',
    styleUrls: ['../../../sharedStyles/base.scss', '../../../sharedStyles/telerik.scss', './heatmap-editor.component.scss']
})
export class HeatmapEditorComponent {
    protected esriLayer:  IMapcoreFeatureLayer;

    protected _heatmapRenderer: __esri.HeatmapRenderer;
    protected _uniqueLayerId: string;
    protected layerService: LayerService;
    protected heatmapService: HeatmapService;
    protected _blurRadius = 0;
    protected _minPixelIntensity = 10;
    protected _maxPixelIntensity = 400;
    protected _rangeSliderValue: [number, number] = [this.minPixelIntensity, this.maxPixelIntensity];
    protected _heatmapMode = HeatmapMode.Off;
    private surface: Surface;

    constructor(_vcr: ViewContainerRef, layerService: LayerService, heatmapService: HeatmapService) {
        this.layerService = layerService;
        this.heatmapService = heatmapService;
    }

    @Input() set uniqueLayerId(value: string) {
        this._uniqueLayerId = value;
        if (value) {
            this.esriLayer = this.layerService.getEsriLayerByUniqueId(this.uniqueLayerId) as IMapcoreFeatureLayer;
            this.heatmapMode = this.esriLayer.mapcoreProps.heatmapMode;
        }
    }
    get uniqueLayerId(): string {
        return this._uniqueLayerId;
    }

    @Input() set heatmapRenderer(value: NonNullable<__esri.HeatmapRenderer>) {
        this._heatmapRenderer = value;
        this.bindForm();
    }
    get heatmapRenderer(): __esri.HeatmapRenderer {
        return this._heatmapRenderer;
    }

    set blurRadius(value: number) {
        this._blurRadius = value;
        if (value && this.esriLayer) {
            this.heatmapRenderer.blurRadius = this.blurRadius;
        }
    }
    get blurRadius(): number {
        return this._blurRadius;
    }
    set minPixelIntensity(value: number) {
        this._minPixelIntensity = value;
        if (value && this.esriLayer) {
            this.heatmapRenderer.minPixelIntensity = this.minPixelIntensity;
        }
    }
    get minPixelIntensity(): number {
        return this._minPixelIntensity;
    }

    set maxPixelIntensity(value: number) {
        this._maxPixelIntensity = value;
        if (value && this.esriLayer) {
            this.heatmapRenderer.maxPixelIntensity = this.maxPixelIntensity;
        }
    }
    get maxPixelIntensity(): number {
        return this._maxPixelIntensity;
    }
    set rangeSliderValue(value: [number, number]) {
        this._rangeSliderValue = value;
        this.minPixelIntensity = this.rangeSliderValue[0];
        this.maxPixelIntensity = this.rangeSliderValue[1];
        if (value && this.esriLayer) {
            this.heatmapRenderer.minPixelIntensity = this.rangeSliderValue[0];
            this.heatmapRenderer.maxPixelIntensity = this.rangeSliderValue[1];
        }
    }

    get rangeSliderValue(): [number, number] {
        return this._rangeSliderValue;
    }

    set heatmapMode(value: HeatmapMode) {
        this._heatmapMode = value;
    }
    get heatmapMode() {
        return this._heatmapMode;
    }

    startColorRamp: string;
    endColorRamp: string;
    colorProperties: string[] = ['symbolColor, outlineColor'];

    bindForm() {
        if (this.heatmapRenderer) {
            if (this.heatmapRenderer.type === 'heatmap') {
                const renderer = this.heatmapRenderer;
                this.blurRadius = this.heatmapRenderer.blurRadius;
                this.startColorRamp = `rgb(${renderer.colorStops.map(x => x.color)[1].toRgb().join(', ')})`;
                this.endColorRamp = `rgb(${renderer.colorStops.map(x => x.color)[2].toRgb().join(', ')})`;
                this.updateColorRamp();
            }
        }
    }

    async updateColorFromPicker(evt, colorProperty) {
        this[colorProperty] = evt;
        if (this.esriLayer && evt) {
            const rgbColor = await this.heatmapService.getColorString(evt, false);
            const initColor = await this.heatmapService.getColorString(evt, true, true);
            const rgbaColor = await this.heatmapService.getColorString(evt, true);
            this.heatmapRenderer.colorStops[colorProperty].color.setColor(rgbaColor);
            if (colorProperty === '1') {
                this.startColorRamp = rgbColor;
                this.heatmapRenderer.colorStops[0].color.setColor(initColor);
            } else {
                this.endColorRamp = rgbColor;
            }
            this.updateColorRamp();

        }
    }
    updateColorRamp() {
        if (this.surface) {
            this.heatmapService.drawScene(this.surface, this.startColorRamp, this.endColorRamp);
        }
    }

    heatmapToggle(heatmapMode: HeatmapMode) {
        this.esriLayer.mapcoreProps.heatmapMode = heatmapMode;

    }
    async onHeatmapModeChange() {
        if (this.esriLayer) {
            this.esriLayer.mapcoreProps.heatmapMode = this.heatmapMode;
            this.esriLayer = await this.heatmapService.updateHeatmapLayers(this.esriLayer);
        }
    }
    @ViewChild('myCanvas') set myCanvas(canvas: ElementRef) {
        if (canvas) {
            const element = canvas.nativeElement;
            this.surface = Surface.create(element, { width: '78.5%', height: '36', type: 'canvas'});
            this.updateColorRamp();
        }
    }

    @Input()
    public applyChange = () => {
        return this.heatmapRenderer;
    }
}
